import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel, HttpTransportType } from '@microsoft/signalr';
import { OAuthService, OAuthStorage } from 'angular-oauth2-oidc';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class SignalRLogoutService implements OnDestroy {
  private hubConnection: HubConnection;
  private reconnectInterval: any;
  private readonly RECONNECT_CHECK_INTERVAL = 30000; // 30 sekund

  constructor(private authStorage: OAuthStorage, private oauthService: OAuthService, private router: Router) {
    this.initializeConnection();
  }

  private initializeConnection(): void {
    this.hubConnection = new HubConnectionBuilder()
      .withUrl(`${environment.gatewayUrl}hubs/logout`, {
        accessTokenFactory: async () => {
          const token = this.authStorage.getItem('access_token');
          return token || '';
        },
        withCredentials: false,
        transport: HttpTransportType.LongPolling,
        skipNegotiation: false
      })
      .configureLogging(environment.production ? LogLevel.Warning : LogLevel.Information)
      .withAutomaticReconnect()
      .build();

    this.setupListeners();
  }

  private setupListeners(): void {
    this.hubConnection.on('logout', () => {
      this.oauthService.revokeTokenAndLogout().then(() => {
        console.log('Token revoked and logged out');
      });
    });

    this.hubConnection.onreconnected(() => {
      console.log('SignalR reconnected successfully');
    });

    this.hubConnection.onreconnecting((error) => {
      console.log('SignalR reconnecting...', error);
    });

    this.hubConnection.onclose((error) => {
      console.log('SignalR connection closed', error);
      setTimeout(() => this.attemptReconnect(), 10000);
    });
  }

  public start(): void {
    const token = this.authStorage.getItem('access_token');
    if (!token) {
      console.log('SignalR: No access token available yet, skipping connection attempt');
      return;
    }

    if (this.hubConnection.state === HubConnectionState.Disconnected) {
      this.hubConnection.start()
        .then(() => {
          console.log('SignalR connection started successfully');
          this.startConnectionMonitoring();
        })
        .catch(err => {
          if (err.name === 'AbortError' || err.message?.includes('abort')) {
            return;
          }
          console.warn('SignalR connection error:', err);
          setTimeout(() => this.attemptReconnect(), 10000);
        });
    } else {
      this.startConnectionMonitoring();
    }
  }

  private startConnectionMonitoring(): void {
    if (this.reconnectInterval) {
      clearInterval(this.reconnectInterval);
    }

    this.reconnectInterval = setInterval(() => {
      if (this.hubConnection.state === HubConnectionState.Disconnected) {
        console.log('SignalR disconnected. Attempting to reconnect...');
        this.attemptReconnect();
      }
    }, this.RECONNECT_CHECK_INTERVAL);
  }

  private attemptReconnect(): void {
    const token = this.authStorage.getItem('access_token');
    if (!token) {
      console.log('SignalR: No access token available, skipping reconnect attempt');
      return;
    }

    if (this.hubConnection.state === HubConnectionState.Disconnected) {
      this.hubConnection.start()
        .then(() => {
          console.log('SignalR reconnect successful');
        })
        .catch(err => {
          if (err.name === 'AbortError' || err.message?.includes('abort')) {
            return;
          }
          console.warn('SignalR reconnect failed:', err);
        });
    }
  }

  public stop(): void {
    if (this.reconnectInterval) {
      clearInterval(this.reconnectInterval);
    }

    if (this.hubConnection.state !== HubConnectionState.Disconnected) {
      this.hubConnection.stop()
        .catch(err => console.warn('SignalR stop error:', err));
    }
  }

  public isConnected(): boolean {
    return this.hubConnection.state === HubConnectionState.Connected;
  }

  ngOnDestroy(): void {
    this.stop();
  }
}
